// ============================================================================
// BOQ — Rate escalation (inflation / cost-index adjustment)
// ============================================================================
// Construction rates are recorded against a `rate_date`. To reuse an old rate
// in a new BOQ it must be escalated to the BOQ's pricing date. The correct QS
// instrument is a CONSTRUCTION cost index (steel/cement/labour basket), NOT
// the general consumer CPI — so escalation runs against boq.cost_index_yearly,
// a yearly index series per region ('IN', 'NL', ...).
//
//   adjusted = original × index(targetDate) / index(rateDate)
//
// The index is yearly; dates are interpolated linearly between adjacent years
// and held flat (no wild extrapolation) outside the series' range. All maths is
// pure here so it can be unit-tested and reused; data loading lives in
// useBoqCostIndex.
// ============================================================================

// Convert a 'YYYY-MM-DD' (or Date) into a fractional year, e.g. 2025-07-02 ≈
// 2025.50. Used to interpolate the yearly index at a sub-year resolution.
export function toFractionalYear(dateLike) {
  if (!dateLike) return null;
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  const year = d.getUTCFullYear();
  const startOfYear = Date.UTC(year, 0, 1);
  const startOfNextYear = Date.UTC(year + 1, 0, 1);
  const frac = (d.getTime() - startOfYear) / (startOfNextYear - startOfYear);
  return year + frac;
}

// Given a sorted [{ year, value }] series, return the (interpolated) index
// value at a fractional year. Flat-held below the first / above the last point.
export function indexAtFractionalYear(series, fracYear) {
  if (!Array.isArray(series) || series.length === 0 || fracYear == null) return null;
  const sorted = [...series].sort((a, b) => a.year - b.year);
  if (fracYear <= sorted[0].year) return sorted[0].value;
  if (fracYear >= sorted[sorted.length - 1].year) return sorted[sorted.length - 1].value;
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const lo = sorted[i];
    const hi = sorted[i + 1];
    if (fracYear >= lo.year && fracYear <= hi.year) {
      const span = hi.year - lo.year;
      if (span === 0) return lo.value;
      const t = (fracYear - lo.year) / span;
      return lo.value + t * (hi.value - lo.value);
    }
  }
  return sorted[sorted.length - 1].value;
}

// The escalation factor (target/from). Returns null when the series can't price
// either date (no data) so callers can fall back to the original rate.
export function escalationFactor(series, fromDate, toDate) {
  const fromIdx = indexAtFractionalYear(series, toFractionalYear(fromDate));
  const toIdx = indexAtFractionalYear(series, toFractionalYear(toDate));
  if (fromIdx == null || toIdx == null || fromIdx <= 0) return null;
  return toIdx / fromIdx;
}

// Escalate a single rate. Returns null when input is missing or the index can't
// price the dates — the caller then keeps the original (no silent zeroing).
export function escalateRate(value, series, fromDate, toDate) {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return null;
  const factor = escalationFactor(series, fromDate, toDate);
  if (factor == null) return null;
  return Number(value) * factor;
}

// Map an app display/contract currency to a cost-index region. INR work prices
// off the India series, EUR work off the Netherlands series.
export function regionForCurrency(currency) {
  return currency === 'EUR' ? 'NL' : 'IN';
}
